import React from 'react';

const RISK_BADGE = {
  low: { bg: '#dcfce7', color: '#166534', label: 'LOW RISK' },
  moderate: { bg: '#fef3c7', color: '#92400e', label: 'MODERATE' },
  elevated: { bg: '#ffedd5', color: '#9a3412', label: 'ELEVATED' },
  high: { bg: '#fee2e2', color: '#991b1b', label: 'HIGH RISK' },
};

const STATUS_COLORS = {
  normal: '#16a34a',
  warning: '#f59e0b',
  critical: '#dc2626',
};

const TREND_CONFIG = {
  stable: { color: '#6b7280', arrow: '', label: 'Stable' },
  accelerating: { color: '#dc2626', arrow: '\u2191', label: 'Accelerating' },
  decelerating: { color: '#16a34a', arrow: '\u2193', label: 'Decelerating' },
};

function getGaugeColor(score) {
  if (score <= 25) return '#16a34a';
  if (score <= 50) return '#f59e0b';
  if (score <= 75) return '#f97316';
  return '#dc2626';
}

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
  },
  badge: {
    fontSize: '13px',
    fontWeight: 700,
    padding: '5px 14px',
    borderRadius: '9999px',
    letterSpacing: '0.03em',
  },
  gaugeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  gaugeTrack: {
    flex: 1,
    height: '8px',
    background: '#f3f4f6',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  gaugeFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.4s ease',
  },
  gaugeLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    whiteSpace: 'nowrap',
  },
  indicatorGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '12px',
    marginBottom: '16px',
  },
  indicatorCard: {
    background: '#f9fafb',
    padding: '12px',
    borderRadius: '6px',
  },
  indicatorName: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '4px',
  },
  indicatorValue: {
    fontSize: '20px',
    fontWeight: 700,
    marginBottom: '2px',
  },
  indicatorThreshold: {
    fontSize: '10px',
    color: '#9ca3af',
    marginBottom: '4px',
  },
  indicatorDesc: {
    fontSize: '11px',
    color: '#6b7280',
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  trendRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '12px',
    fontSize: '13px',
    fontWeight: 500,
  },
  summary: {
    fontSize: '13px',
    color: '#4b5563',
    fontStyle: 'italic',
    lineHeight: 1.5,
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
    fontSize: '14px',
  },
};

export default function PriceWarPanel({ data }) {
  if (!data) {
    return (
      <div style={styles.card}>
        <div style={styles.emptyState}>No price war data available</div>
      </div>
    );
  }

  const { overallRisk, riskScore, indicators, recentTrend, summary } = data;
  const riskBadge = RISK_BADGE[overallRisk] || RISK_BADGE.low;
  const gaugeColor = getGaugeColor(riskScore);
  const trend = TREND_CONFIG[recentTrend] || TREND_CONFIG.stable;

  return (
    <div style={styles.card}>
      {/* Header */}
      <div style={styles.headerRow}>
        <span style={styles.title}>Price War Early Warning</span>
        <span
          style={{
            ...styles.badge,
            background: riskBadge.bg,
            color: riskBadge.color,
          }}
        >
          {riskBadge.label}
        </span>
      </div>

      {/* Risk Score Gauge */}
      <div style={styles.gaugeContainer}>
        <div style={styles.gaugeTrack}>
          <div
            style={{
              ...styles.gaugeFill,
              width: `${Math.min(Math.max(riskScore, 0), 100)}%`,
              background: gaugeColor,
            }}
          />
        </div>
        <span style={styles.gaugeLabel}>{riskScore}/100</span>
      </div>

      {/* Indicator Cards */}
      <div style={styles.indicatorGrid}>
        {(indicators || []).map((indicator, idx) => {
          const statusColor = STATUS_COLORS[indicator.status] || STATUS_COLORS.normal;
          return (
            <div
              key={idx}
              style={{
                ...styles.indicatorCard,
                borderLeft: `3px solid ${statusColor}`,
              }}
            >
              <div style={styles.indicatorName}>{indicator.name}</div>
              <div style={{ ...styles.indicatorValue, color: statusColor }}>
                {indicator.value}
              </div>
              <div style={styles.indicatorThreshold}>threshold: {indicator.threshold}</div>
              <div style={styles.indicatorDesc}>{indicator.description}</div>
            </div>
          );
        })}
      </div>

      {/* Trend Indicator */}
      <div style={{ ...styles.trendRow, color: trend.color }}>
        {trend.arrow && <span style={{ fontSize: '15px' }}>{trend.arrow}</span>}
        <span>Trend: {trend.label}</span>
      </div>

      {/* Summary */}
      {summary && <div style={styles.summary}>{summary}</div>}
    </div>
  );
}
